import { Checkbox, Col, Row, Select } from "antd";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { Project } from "../../../types";
import { SpaceCategorySelector } from "../../Selectors/SpaceCategorySelector";
import { SpaceTagsSelector } from "../../Selectors/SpaceTagsSelector";
import { SpaceTypeSelector } from "../../Selectors/SpaceTypeSelector";
import styles from "./styles.module.css";

export type SpaceSearchInput = {
  terms: string[];
  skipLevels: boolean;
  categories: string[];
  spaceTypes: string[];
  tags: string[];
};

type UpdateName = {
  type: "UPDATE_TERMS";
  payload: string[];
};

type UpdateSkipLevels = {
  type: "UPDATE_SKIP_LEVELS";
  payload: boolean;
};

type UpdateCategories = {
  type: "UPDATE_CATEGORIES";
  payload: string[];
};

type UpdateSpaceTypes = {
  type: "UPDATE_SPACE_TYPES";
  payload: string[];
};

type UpdateTags = {
  type: "UPDATE_TAGS";
  payload: string[];
};

export const initialSearchInput: SpaceSearchInput = {
  terms: [],
  skipLevels: false,
  categories: [],
  spaceTypes: [],
  tags: [],
};

type UpdateSearchInput =
  | UpdateName
  | UpdateSkipLevels
  | UpdateCategories
  | UpdateSpaceTypes
  | UpdateTags;

const searchInputReducer = (
  state: SpaceSearchInput,
  action: UpdateSearchInput,
): SpaceSearchInput => {
  switch (action.type) {
    case "UPDATE_TERMS":
      return { ...state, terms: action.payload };
    case "UPDATE_SKIP_LEVELS":
      return { ...state, skipLevels: action.payload };
    case "UPDATE_CATEGORIES":
      return { ...state, categories: action.payload };
    case "UPDATE_SPACE_TYPES":
      return { ...state, spaceTypes: action.payload };
    case "UPDATE_TAGS":
      return { ...state, tags: action.payload };
  }
};

type SearchBarProps = {
  project: Project;
  onChange: (value: SpaceSearchInput) => any;
};

export const SearchBar = (props: SearchBarProps) => {
  const { project, onChange } = props;
  const [searchInput, setSearchInput] = useReducer(
    searchInputReducer,
    initialSearchInput,
  );

  const debouncedOnChange = useMemo(
    () =>
      _.debounce((value: SpaceSearchInput) => {
        onChange(value);
      }, 300),
    [onChange],
  );

  useEffect(() => {
    debouncedOnChange(searchInput);
    return () => {
      debouncedOnChange.cancel();
    };
  }, [searchInput, debouncedOnChange]);

  const updateTerms = useCallback((name: string[]) => {
    setSearchInput({ type: "UPDATE_TERMS", payload: name });
  }, []);

  const updateSkipLevels = useCallback((skipLevels: boolean) => {
    setSearchInput({ type: "UPDATE_SKIP_LEVELS", payload: skipLevels });
  }, []);

  const updateCategories = useCallback((values: string[]) => {
    setSearchInput({ type: "UPDATE_CATEGORIES", payload: values });
  }, []);

  const updateSpaceTypes = useCallback((values: string[]) => {
    setSearchInput({ type: "UPDATE_SPACE_TYPES", payload: values });
  }, []);

  const updateTags = useCallback((values: string[]) => {
    setSearchInput({ type: "UPDATE_TAGS", payload: values });
  }, []);

  return (
    <>
      <Row>
        <Col span={24}>
          <SearchBox
            onChange={updateTerms}
            placeholder="Search by name or hierarchy (e.g., Building<>Floor<>Room)..."
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Checkbox
            checked={searchInput.skipLevels}
            onChange={(e) => updateSkipLevels(e.target.checked)}
          >
            Skip hierarchy levels (e.g. &quot;Tower 1&lt;&gt;Riser&quot; = all
            risers in Tower 1, not specifically the riser right under Tower 1)
          </Checkbox>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <SpaceCategorySelector
            project={project}
            placeholder="Category"
            selectedCategories={searchInput.categories}
            onChange={updateCategories}
          />
        </Col>
        <Col span={8}>
          <SpaceTypeSelector
            project={project}
            selectedTypes={searchInput.spaceTypes}
            placeholder="Space Type"
            onChange={updateSpaceTypes}
          />
        </Col>
        <Col span={8}>
          <SpaceTagsSelector
            project={props.project}
            selectedTags={searchInput.tags}
            placeholder="Tags"
            onChange={updateTags}
          />
        </Col>
      </Row>
    </>
  );
};

type SearchBoxProps = {
  onChange: (value: string[]) => void;
  placeholder?: string;
};

const SearchBox = (props: SearchBoxProps) => (
  <Select
    className={styles["search-box"]}
    mode="tags"
    onChange={props.onChange}
    placeholder={props.placeholder || "Search..."}
  />
);
